import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="deliverables-upload"
export default class extends Controller {
  static targets = ["input", "fileList", "progress", "progressBar", "dropzone", "contentType"]
  static values = {
    imageAccept: { type: String, default: "" },
    videoAccept: { type: String, default: "" },
    imageType: { type: String, default: "" },
    videoType: { type: String, default: "" }
  }

  connect() {
    this.uploadProgress = {}
    this.uploadTotal = 0
    this.uploadedCount = 0
    this.selectedFiles = new Set()
    this.selectedFilesData = new DataTransfer()

    this.initDirectUploadListeners()
  }

  initDirectUploadListeners() {
    this.inputTarget.addEventListener("direct-upload:initialize", event => {
      this.showProgress()
      this.initializeUpload(event)
    })

    this.inputTarget.addEventListener("direct-upload:start", event => {
      this.startUpload(event)
    })

    this.inputTarget.addEventListener("direct-upload:progress", event => {
      this.progressUpload(event)
    })

    this.inputTarget.addEventListener("direct-upload:error", event => {
      this.errorUpload(event)
    })

    this.inputTarget.addEventListener("direct-upload:end", event => {
      this.endUpload(event)
    })
  }

  triggerFileInput() {
    this.inputTarget.click()
  }

  handleFileSelect() {
    this.updateAcceptedFileTypesFromFirstFile()

    // 새로운 파일들 추가
    Array.from(this.inputTarget.files).forEach(file => {
      this.selectedFilesData.items.add(file)
    })

    // input의 files를 업데이트
    this.inputTarget.files = this.selectedFilesData.files
    this.updateFileList()
  }

  updateAcceptedFileTypesFromFirstFile() {
    const files = Array.from(this.inputTarget.files);
    if (files.length > 0) {
      const firstFile = files[0];

      if (firstFile.type.startsWith('image/')) {
        this.inputTarget.accept = this.imageAcceptValue;
        this.contentTypeTarget.value = this.imageTypeValue;
      } else if (firstFile.type.startsWith('video/')) {
        this.inputTarget.accept = this.videoAcceptValue;
        this.contentTypeTarget.value = this.videoTypeValue;
      }
    }
  }

  updateFileList() {
    this.fileListTarget.innerHTML = ''
    this.selectedFiles.clear()

    Array.from(this.inputTarget.files).forEach(file => {
      const fileId = this.generateFileId(file)
      this.selectedFiles.add(fileId)

      const fileItem = this.createFileItem(fileId, file)
      this.fileListTarget.appendChild(fileItem)
    })
  }

  createFileItem(fileId, file) {
    const fileItem = document.createElement('div')
    fileItem.className = 'flex items-center justify-between px-3.5 py-2 mb-2 bg-gray-50 rounded border border-gray-200'
    fileItem.id = `file-${fileId}`

    const fileInfo = this.createFileInfo(file)
    const removeButton = this.createRemoveButton(fileId, file.name)

    fileItem.appendChild(fileInfo)
    fileItem.appendChild(removeButton)

    return fileItem
  }

  createFileInfo(file) {
    const fileInfo = document.createElement('div')
    fileInfo.className = 'flex items-center space-x-3'

    // 파일 타입에 따른 아이콘
    const icon = this.createIcon(file.type)
    const nameSize = this.createNameSize(file)

    fileInfo.appendChild(icon)
    fileInfo.appendChild(nameSize)

    return fileInfo
  }

  createIcon(fileType) {
    const icon = document.createElement('span')
    icon.className = 'text-gray-500'
    icon.innerHTML = this.getFileIcon(fileType)
    return icon
  }

  createNameSize(file) {
    const nameSize = document.createElement('div')
    // TODO: truncate를 고려하여 width(w-40) 조정 필요
    nameSize.innerHTML = `
      <div class="w-40 text-sm font-medium truncate">
        ${file.name}
      </div>
      <div class="text-xs text-gray-500">
        ${this.formatFileSize(file.size)}
      </div>
    `
    return nameSize
  }

  createRemoveButton(fileId, fileName) {
    const removeButton = document.createElement('button')
    removeButton.type = 'button'
    removeButton.className = 'text-red-500 hover:text-red-700'
    removeButton.dataset.action = 'click->deliverables-upload#removeFile'
    removeButton.dataset.deliverablesUploadFileIdParam = fileId
    removeButton.dataset.deliverablesUploadFileNameParam = fileName
    removeButton.innerHTML = '&times;'

    return removeButton
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add('border-blue-500', 'bg-blue-50')
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50')
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove('border-blue-500', 'bg-blue-50')

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.inputTarget.files = files
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  ////////////////////////////////
  initializeUpload(event) {
    const { id, file } = event.detail
    this.uploadProgress[id] = 0
    this.uploadTotal++
  }

  startUpload(event) {
    const { id } = event.detail
    this.progressBarTarget.style.width = `0%`
  }

  progressUpload(event) {
    const { id, progress } = event.detail
    this.uploadProgress[id] = progress
    this.updateTotalProgress()
  }

  errorUpload(event) {
    const { id, error } = event.detail
    console.error(`Error uploading file: ${error}`)
    this.hideProgress()
  }

  endUpload(event) {
    const { id } = event.detail
    this.uploadedCount++
  }

  updateTotalProgress() {
    const progress = Object.values(this.uploadProgress)
    const totalProgress = progress.reduce((total, current) => total + current, 0)
    const averageProgress = totalProgress / this.uploadTotal
    this.progressBarTarget.style.width = `${averageProgress}%`
  }

  showProgress() {
    this.progressTarget.classList.remove('hidden')
    this.progressTarget.classList.add('block')
  }

  hideProgress() {
    this.progressTarget.classList.remove('block')
    this.progressTarget.classList.add('hidden')
  }

  ////////////////////////////////
  generateFileId(file) {
    return `${file.name}-${file.size}-${file.lastModified}`
  }

  getFileIcon(fileType) {
    if (fileType.startsWith('image/')) {
      return this.imageIcon
    }
    if (fileType.startsWith('video/')) {
      return this.videoIcon
    }
    if (fileType === 'application/pdf') {
      return this.pdfIcon
    }
    return this.attachmentIcon
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  removeFile({params: { fileId, fileName }}) {
    const fileItem = document.getElementById(`file-${fileId}`)

    if (!fileItem) { return }

    fileItem.remove()
    this.selectedFiles.delete(fileId)

    this.selectedFilesData = new DataTransfer()

    Array.from(this.inputTarget.files).forEach(file => {
      const currentFileId = this.generateFileId(file)
      if (currentFileId !== fileId) {
        this.selectedFilesData.items.add(file)
      }
    })

    this.inputTarget.files = this.selectedFilesData.files
    this.clearAcceptedFileTypes()
  }

  clearAcceptedFileTypes() {
    if (this.inputTarget.files.length === 0) {
      this.inputTarget.accept = 'image/jpg,image/jpeg,image/png,image/gif,video/mp4,video/mov,video/avi';
      this.contentTypeTarget.value = '';
    }
  }

  ////////////////////////////////
  // Icons
  get imageIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>'
  }

  get videoIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0 1 18 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 7.746 6 7.125v-1.5M4.875 8.25C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0 1 18 7.125v-1.5m1.125 2.625c-.621 0-1.125.504-1.125 1.125v1.5m2.625-2.625c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125M18 5.625v5.25M7.125 12h9.75m-9.75 0A1.125 1.125 0 0 1 6 10.875M7.125 12C6.504 12 6 12.504 6 13.125m0-2.25C6 11.496 5.496 12 4.875 12M18 10.875c0 .621-.504 1.125-1.125 1.125M18 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m-12 5.25v-5.25m0 5.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125m-12 0v-1.5c0-.621-.504-1.125-1.125-1.125M18 18.375v-5.25m0 5.25v-1.5c0-.621.504-1.125 1.125-1.125M18 13.125v1.5c0 .621.504 1.125 1.125 1.125M18 13.125c0-.621.504-1.125 1.125-1.125M6 13.125v1.5c0 .621-.504 1.125-1.125 1.125M6 13.125C6 12.504 5.496 12 4.875 12m-1.5 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M19.125 12h1.5m0 0c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h1.5m14.25 0h1.5" /></svg>'
  }

  get pdfIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" /></svg>'
  }

  get attachmentIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6"><path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" /></svg>'
  }
}
